/**
 * Send: the reverse arrow. Put an HL7 message on an outbound channel and show
 * what the peer said back.
 *
 * Target, timeout and retries are read-only. The Iguana Simulator lets an
 * operator type them; here they belong to the channel table, because the
 * evidence record for a run has to state what timeout was in force and it
 * cannot if each send picked its own.
 *
 * Deliberately absent: batch send, time delay, "treat any incoming data as an
 * ACK", and ACK-via-server-socket. All four are in the reference screenshot and
 * none are built — we require a real MSA whose MSA-2 echoes the MSH-10 we sent.
 *
 * Two failure shapes have to render the same way. A completed exchange the peer
 * rejected is HTTP 200 with `ok: false` and a reason; a malformed message,
 * unknown channel or wrong direction is 400/404/409 with a message. Neither is
 * a crash and both are ordinary output for this screen.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, Loader2, SendHorizontal } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { dataLayerApi } from '@/api/dataLayerApi'
import { countDisconnections, countTimeouts } from '@/lib/dataLayer/counters'
import { messageTypeCode, toEditorText, toWireEr7, wireSegments } from '@/lib/dataLayer/er7'
import { outboundChannelLabel, outboundChannels } from '@/lib/dataLayer/channelView'
import { useDataLayerTail } from '@/hooks/useDataLayerTail'
import type { DataLayerFailure } from '@/lib/dataLayer/errors'
import type { Hl7Channel, Hl7SendResult } from '@/lib/dataLayer/types'
import { MessageDetailDialog } from './MessageDetailDialog'
import { PacketLog } from './PacketLog'

// Fallback while the fixture list loads. Names, not types: the synthetic three
// are named after their type, so these double as valid names.
const FIXTURES = ['ADT', 'ORM', 'ORU']

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-0.5 truncate font-mono text-xs">{value}</p>
    </div>
  )
}

export function SendPanel({ channels }: { channels: Hl7Channel[] }) {
  const { can } = usePermission()
  const canManage = can(PERMISSIONS.integrationsManage)

  const outbound = outboundChannels(channels)
  const [channelId, setChannelId] = useState('')
  const [fixture, setFixture] = useState('ORU')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<DataLayerFailure | null>(null)
  const [result, setResult] = useState<Hl7SendResult | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const selected = outbound.find((c) => c.id === channelId) ?? outbound[0] ?? null

  const { data: fixtures } = useQuery({
    queryKey: ['data-layer', 'fixtures'],
    queryFn: () => dataLayerApi.listFixtures(),
    retry: false,
  })

  const { tail, isLoading } = useDataLayerTail({ direction: 'out' }, true)

  async function loadFixture() {
    const outcome = await dataLayerApi.getFixture(fixture).then(
      (value) => ({ ok: true as const, value }),
      () => ({ ok: false as const }),
    )
    // The fixture arrives CR-separated, but a textarea reports LF back the moment
    // React writes it, so hold LF in state and convert once at the wire. Without
    // this, editing a loaded fixture silently changes its separators.
    if (outcome.ok) setMessage(toEditorText(outcome.value.raw))
  }

  async function send() {
    if (!selected) return
    setBusy(true)
    setFailure(null)
    setResult(null)

    // Exactly one of message/fixture. Note `peer` is legal on mimic/inbound and
    // rejected here, so the two bodies cannot share a builder.
    //
    // toWireEr7 also subsumes the old emptiness check: it returns '' for blank or
    // whitespace-only input, which is exactly when the fixture should be used.
    const wire = toWireEr7(message)
    const body = wire
      ? { channelId: selected.id, message: wire }
      : { channelId: selected.id, fixture }

    const outcome = await dataLayerApi.send(body)
    setBusy(false)

    if (!outcome.ok) {
      setFailure(outcome.failure)
      return
    }
    setResult(outcome.value)
  }

  // The shipped channels.json carries inbound channels only, so this is the
  // common case for a locally started harness. The fix is a different command,
  // not a configuration change. It cannot arise on the deployed mock server,
  // which ships both outbound channels, hence the local-only wording.
  if (outbound.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center">
        <p className="text-sm font-medium">No outbound channels configured</p>
        <p className="mx-auto mt-1 max-w-lg text-sm text-muted-foreground">
          This data layer's channel table has inbound channels only, so there is nothing to send on.
          If it is a local harness, restart it with the dev table, which points the outbound channels
          back at its own listeners:
        </p>
        <pre className="mx-auto mt-3 inline-block rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-left font-mono text-xs">
          npm run mllp:dev
        </pre>
      </div>
    )
  }

  const mismatch =
    result?.acknowledgedControlId !== undefined &&
    result.acknowledgedControlId !== result.controlId

  const segments = wireSegments(message)

  const timeouts = countTimeouts(tail.messages)
  const disconnections = countDisconnections(tail.messages)

  // Non-blocking on purpose. /send does not check MSH-9 against the channel's
  // declared types and the loopback answers AA regardless, so an ORU sent on
  // orm-out looks entirely successful. This is the only place that says so, and
  // an operator may still have a reason to do it.
  const messageType = messageTypeCode(message)
  const wrongChannelType =
    messageType !== null && selected !== null && !selected.types.includes(messageType)

  return (
    <div className="space-y-4">
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card className="border-border/60 shadow-none">
          <CardContent className="space-y-4 p-4">
            <div className="space-y-1.5">
              <Label htmlFor="send-channel">Outbound channel</Label>
              <Select value={selected?.id ?? ''} onValueChange={setChannelId}>
                <SelectTrigger id="send-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* A plain string, not JSX children: the trigger is a bare
                      <SelectValue />, which mirrors the selected item, so the
                      closed label carries the port for free. */}
                  {outbound.map((channel) => (
                    <SelectItem key={channel.id} value={channel.id}>
                      {outboundChannelLabel(channel)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selected && (
              // Read-only by design; see the file header.
              <div className="grid grid-cols-3 gap-3 rounded-lg border border-border/60 bg-muted/20 p-3">
                <Field label="Target" value={selected.target} />
                <Field label="Timeout" value={`${selected.timeoutMs}ms`} />
                <Field label="Retries" value={selected.retries} />
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="send-fixture">Sample message</Label>
              <div className="flex items-center gap-2">
                <Select value={fixture} onValueChange={setFixture}>
                  <SelectTrigger id="send-fixture">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Keyed by NAME: several fixtures share a type (six BEAKER-* are
                        all ORU), and duplicate Select values mark every twin selected
                        and concatenate their labels in the trigger. */}
                    {fixtures?.fixtures.map((f) => (
                      <SelectItem key={f.name} value={f.name}>
                        {f.name} · {f.description}
                      </SelectItem>
                    )) ??
                      FIXTURES.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={loadFixture}>
                  <Download className="mr-2 h-3.5 w-3.5" />
                  Load
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Loading restamps the control id and timestamp each time.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="send-message">Message</Label>
              <Textarea
                id="send-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Load a sample, or paste an HL7 message here."
                className="min-h-[180px] font-mono text-xs"
              />
              {/* A segment count rather than a "we changed your message" banner.
                  A textarea cannot hold CR, so such a banner would fire on every
                  message anyone ever pastes and become chrome. The count is a
                  checkable fact, and it catches a real paste failure: copying
                  from a rendered markdown viewer that hard-wraps the long OBR
                  line yields one segment too many. */}
              <p className="text-xs text-muted-foreground">
                {segments.length > 0 && (
                  <>
                    {segments.length} segment{segments.length === 1 ? '' : 's'}, separated by CR
                    (0x0D) on the wire.{' '}
                  </>
                )}
                Line breaks typed here are converted on send.
              </p>
              {/* No article before {messageType}: it is whatever MSH-9.1 says,
                  so "a"/"an" cannot be chosen correctly here. */}
              {wrongChannelType && (
                <p className="text-xs text-amber-700">
                  This message declares {messageType} and {selected?.id} carries{' '}
                  {selected?.types.join(', ')}. Sending is still allowed, and the peer will
                  acknowledge it, so nothing else will flag this.
                </p>
              )}
            </div>

            {/* "Send message", not "Send": the mode switch above already has a
                Send button, and two identically named controls on one panel are
                ambiguous to a screen reader and to a test locator. */}
            {canManage && (
              <Button onClick={send} disabled={busy || !selected}>
                {busy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <SendHorizontal className="mr-2 h-4 w-4" />
                )}
                Send message
              </Button>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-none">
          <CardContent className="space-y-3 p-4">
            <h4 className="text-sm font-semibold">Last send</h4>

            {failure && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2.5">
                <p className="text-sm font-medium text-destructive">
                  Rejected before sending{failure.status ? ` (${failure.status})` : ''}
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">{failure.message}</p>
              </div>
            )}

            {!failure && !result && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nothing sent yet.
              </p>
            )}

            {result && (
              <>
                <div
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-3 py-2.5',
                    result.ok && !mismatch
                      ? 'border-green-500/30 bg-green-500/5'
                      : 'border-destructive/30 bg-destructive/5',
                  )}
                >
                  <span
                    className={cn(
                      'h-2 w-2 shrink-0 rounded-full',
                      result.ok && !mismatch ? 'bg-green-500' : 'bg-destructive',
                    )}
                  />
                  <span className="text-sm font-medium">
                    {result.ok && !mismatch ? 'Acknowledged' : 'Not acknowledged'}
                  </span>
                  {result.ackCode && <Badge variant="secondary">{result.ackCode}</Badge>}
                </div>

                {result.reason && (
                  <p className="text-sm text-destructive">{result.reason}</p>
                )}

                {/* A peer that echoes a different control id has not
                    acknowledged OUR message, whatever code it returned. */}
                {mismatch && (
                  <p className="text-sm text-destructive">
                    The peer echoed a different control id, so this acknowledges some other message.
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Control id sent" value={result.controlId} />
                  <Field label="Control id echoed" value={result.acknowledgedControlId ?? '—'} />
                  <Field label="Attempts" value={result.attempts} />
                  <Field label="Latency" value={`${result.latencyMs}ms`} />
                </div>

                {/* Read from the send response: messageId is allowed to be
                    empty, so re-fetching by id can come back with nothing. */}
                {result.ack && (
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      Acknowledgment
                    </p>
                    <pre className="max-h-48 overflow-auto rounded-lg border border-border/60 bg-muted/40 p-3 font-mono text-xs">
                      {result.ack
                        .split(/\r\n|\r|\n/)
                        .filter(Boolean)
                        .map((segment, i) => (
                          <div key={i} className="whitespace-pre-wrap break-all">
                            {segment}
                          </div>
                        ))}
                    </pre>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h4 className="text-sm font-semibold">Sent packets</h4>
          {/* The Iguana Simulator's Timeouts and Disconnections. Derived, because
              no endpoint counts them, and scoped to the visible log for the same
              reason as the Receive panel's figure. */}
          {tail.messages.length > 0 && (
            <p className="text-xs text-muted-foreground">
              <span className={timeouts > 0 ? 'font-medium text-amber-700' : undefined}>
                Timeouts {timeouts}
              </span>{' '}
              ·{' '}
              <span className={disconnections > 0 ? 'font-medium text-amber-700' : undefined}>
                Disconnections {disconnections}
              </span>{' '}
              · in the {tail.messages.length} packet{tail.messages.length === 1 ? '' : 's'} shown
            </p>
          )}
        </div>
        <PacketLog
          messages={tail.messages}
          isLoading={isLoading}
          selectedId={selectedId}
          onSelect={(m) => setSelectedId(m.id)}
          emptyHint="Nothing sent yet."
        />
      </div>

      <MessageDetailDialog messageId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  )
}
